// @ts-nocheck
import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Popover,
    Checkbox,
    CircularProgress,
    IconButton,
    Chip,
    InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAreas } from "../../../Areas/State/AreasContext";

export default function SelectListasCompartidas({ areaSeleccionada, areasCompartidas, handleAreasCompartidasChange, rootAreas = [], loadingRootAreas = false }) {
    const { fetchSubgrupos } = useAreas();
    const [anchorEl, setAnchorEl] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [loadingIds, setLoadingIds] = useState(new Set());
    const anchorRef = useRef(null);

    const selectedIds = new Set(areasCompartidas.map(a => a.id));
    const open = Boolean(anchorEl);

    const handleOpen = () => {
        setAnchorEl(anchorRef.current);
        if (nodes.length > 0 || rootAreas.length === 0) return;
        setNodes(
            rootAreas
                .filter(a => a.id !== areaSeleccionada)
                .map(a => ({ ...a, level: 0, expanded: false, loaded: false }))
        );
    };

    const handleExpand = async (e, node) => {
        e.stopPropagation();
        if (node.expanded) {
            setNodes(prev => collapse(prev, node.id));
            return;
        }
        if (node.loaded) {
            setNodes(prev => prev.map(n => n.id === node.id ? { ...n, expanded: true } : n));
            return;
        }
        setLoadingIds(prev => new Set(prev).add(node.id));
        try {
            const subgrupos = await fetchSubgrupos(node);
            const children = subgrupos
                .filter(s => s.id !== areaSeleccionada)
                .map(s => ({ ...s, level: node.level + 1, expanded: false, loaded: false, parentId: node.id }));
            setNodes(prev => {
                const idx = prev.findIndex(n => n.id === node.id);
                const next = [...prev];
                next[idx] = { ...next[idx], expanded: true, loaded: true };
                next.splice(idx + 1, 0, ...children);
                return next;
            });
        } finally {
            setLoadingIds(prev => { const s = new Set(prev); s.delete(node.id); return s; });
        }
    };

    const collapse = (nodes, parentId) => {
        const childIds = nodes.filter(n => n.parentId === parentId).map(n => n.id);
        let result = nodes.map(n => n.id === parentId ? { ...n, expanded: false, loaded: false } : n);
        for (const childId of childIds) {
            result = collapse(result, childId);
        }
        return result.filter(n => n.parentId !== parentId);
    };

    const toggleSelect = (node) => {
        if (selectedIds.has(node.id)) {
            handleAreasCompartidasChange(areasCompartidas.filter(a => a.id !== node.id));
        } else {
            handleAreasCompartidasChange([...areasCompartidas, { id: node.id, name: node.name }]);
        }
    };

    const removeSelected = (id) => {
        handleAreasCompartidasChange(areasCompartidas.filter(a => a.id !== id));
    };

    return (
        <Box>
            <TextField
                ref={anchorRef}
                label="Compartir con áreas"
                variant="outlined"
                fullWidth
                onClick={handleOpen}
                value=""
                onChange={() => {}}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            {loadingRootAreas
                                ? <CircularProgress size={20} color="inherit" />
                                : <ExpandMoreIcon color="action" />
                            }
                        </InputAdornment>
                    ),
                }}
                sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
            />

            {areasCompartidas.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {areasCompartidas.map(area => (
                        <Chip
                            key={area.id}
                            label={area.name}
                            variant="outlined"
                            onDelete={() => removeSelected(area.id)}
                        />
                    ))}
                </Box>
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { width: anchorRef.current?.offsetWidth ?? 400, maxHeight: 360 } }}
            >
                <Box sx={{ overflowY: 'auto', maxHeight: 360 }}>
                    {loadingRootAreas ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : nodes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No se encontraron áreas
                        </Typography>
                    ) : (
                        nodes.map(node => (
                            <Box
                                key={node.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: 1 + node.level * 2,
                                    pr: 1,
                                    py: 0.25,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                                onClick={() => toggleSelect(node)}
                            >
                                {node.subGroupCount > 0 ? (
                                    <IconButton
                                        size="small"
                                        onClick={e => handleExpand(e, node)}
                                        sx={{ mr: 0.5 }}
                                    >
                                        {loadingIds.has(node.id)
                                            ? <CircularProgress size={16} />
                                            : node.expanded
                                                ? <ExpandMoreIcon fontSize="small" />
                                                : <ChevronRightIcon fontSize="small" />
                                        }
                                    </IconButton>
                                ) : (
                                    <Box sx={{ width: 34 }} />
                                )}
                                <Checkbox
                                    size="small"
                                    checked={selectedIds.has(node.id)}
                                    sx={{ color: '#B71234', '&.Mui-checked': { color: '#B71234' } }}
                                    onClick={e => e.stopPropagation()}
                                    onChange={() => toggleSelect(node)}
                                />
                                <Typography variant="body2">{node.name}</Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </Popover>
        </Box>
    );
}
